import { SearchField } from "@majitap/ui/arc/search-field/search-field";
import { useQuery } from "@tanstack/react-query";
import { Hexagon, MapPin, MapPinPlus, Navigation, X } from "lucide-react";
import { type KeyboardEvent, type ReactNode, useId, useRef, useState } from "react";
import { errorOf } from "../../api";
import { messageFor } from "../../error-copy";
import { MIN_QUERY, useDebounced } from "../../search";
import { addressPoint, findAddresses, findSavedPlaces, type LookedAt, type Zone } from "../../zones";
import styles from "./zones.module.css";

type Group = "Zones" | "Saved places" | "Addresses";

interface Option {
  key: string;
  group: Group;
  title: string;
  subtitle: string | null;
  icon: ReactNode;
  /** a tag on the right, as in the design: a zone's status, the zone a place is in */
  tag?: { label: string; tone: "info" | "success" | "neutral" | "danger" };
  pick: () => void;
}

/** Why the address group is empty: no integration yet, or the provider failed. */
export function addressNote(error: unknown): string {
  const { code } = errorOf(error);
  if (code === "INTEGRATION_NOT_CONFIGURED")
    return "Address search is off. Set up Google Places in Settings › Integrations.";
  return messageFor(errorOf(error));
}

/**
 * One search for a zone, a saved place or an address (P8-40, `Console / Service zones / Place
 * search`). Addresses use one session per search, so the provider bills a search, not a keystroke.
 */
export function PlaceSearch({
  zones,
  chosen = null,
  onClear,
  onZone,
  onPlace,
  onDropPin,
}: {
  zones: Zone[];
  /** the place being looked at: shown as a chip in place of the field, as in the design */
  chosen?: string | null;
  onClear?: () => void;
  onZone: (id: string) => void;
  onPlace: (place: LookedAt) => void;
  onDropPin: () => void;
}) {
  const listId = useId();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [note, setNote] = useState<string | null>(null);
  const session = useRef(crypto.randomUUID());
  const typed = query.trim();
  const q = useDebounced(typed);
  const ready = q.length >= MIN_QUERY;

  const saved = useQuery({
    queryKey: ["zones", "search", "saved", q],
    queryFn: () => findSavedPlaces(q),
    enabled: ready,
    staleTime: 30_000,
  });
  const addresses = useQuery({
    queryKey: ["zones", "search", "address", q],
    queryFn: () => findAddresses(q, session.current),
    enabled: ready,
    staleTime: 60_000,
    retry: false,
  });

  const finish = () => {
    setOpen(false);
    setQuery("");
    setNote(null);
    session.current = crypto.randomUUID();
  };

  const zoneOptions: Option[] = zones
    .filter((z) => ready && z.name.toLowerCase().includes(q.toLowerCase()))
    .slice(0, 5)
    .map((z) => ({
      key: `zone-${z.id}`,
      group: "Zones",
      title: z.name,
      subtitle: `Zone · ${z.location_count === 1 ? "1 place" : `${z.location_count} places`}${z.area_km2 ? ` · ${z.area_km2} km²` : ""}`,
      tag: z.status === "active" ? { label: "Active", tone: "success" } : { label: "Off", tone: "neutral" },
      icon: <Hexagon size={15} aria-hidden />,
      pick: () => {
        onZone(z.id);
        finish();
      },
    }));
  const savedOptions: Option[] = ready
    ? (saved.data ?? []).map((p) => ({
        key: `saved-${p.kind === "saved" ? p.id : p.label}`,
        group: "Saved places",
        title: p.label,
        subtitle: p.detail,
        tag:
          p.kind === "saved"
            ? p.zoneName
              ? { label: p.zoneName, tone: "info" as const }
              : { label: "Outside every zone", tone: "danger" as const }
            : undefined,
        icon: <MapPin size={15} aria-hidden />,
        pick: () => {
          onPlace(p);
          finish();
        },
      }))
    : [];
  const addressOptions: Option[] = ready
    ? (addresses.data ?? []).map((a) => ({
        key: `address-${a.place_id}`,
        group: "Addresses",
        title: a.title,
        subtitle: a.subtitle,
        icon: <Navigation size={15} aria-hidden />,
        pick: () => {
          setNote("Finding it on the map…");
          addressPoint(a.place_id, session.current).then(
            (p) => {
              onPlace({ kind: "address", label: p.name, detail: p.address, lng: p.longitude, lat: p.latitude });
              finish();
            },
            (error) => setNote(messageFor(errorOf(error))),
          );
        },
      }))
    : [];
  const options = [...zoneOptions, ...savedOptions, ...addressOptions];

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Escape") return setOpen(false);
    if (!options.length) return;
    const step = { ArrowDown: 1, ArrowUp: -1 }[event.key];
    if (step) {
      event.preventDefault();
      setActive((active + step + options.length) % options.length);
    } else if (event.key === "Enter") {
      event.preventDefault();
      options[active]?.pick();
    }
  };

  const showList = open && typed.length > 0;
  const groups: Group[] = ["Zones", "Saved places", "Addresses"];
  const pending = q !== typed || saved.isFetching || addresses.isFetching;

  if (chosen) {
    return (
      <div className={styles.placeSearch}>
        <div className={styles.chosenField}>
          <span className={styles.chosenChip}>
            <MapPin size={14} aria-hidden />
            {chosen}
          </span>
          <button type="button" className={styles.iconButton} aria-label="Clear the place" onClick={onClear}>
            <X size={14} aria-hidden />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.placeSearch}>
      <SearchField
        label="Find a zone or place"
        placeholder="Find a zone, place or address"
        value={query}
        onValueChange={(v) => {
          setQuery(v);
          setActive(0);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        role="combobox"
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={showList && options[active] ? `${listId}-${options[active].key}` : undefined}
        onKeyDown={onKeyDown}
      />
      {showList ? (
        <div className={styles.dropdown}>
          <div id={listId} role="listbox" aria-label="Results">
            {groups.map((group) => (
              <ResultGroup
                key={group}
                group={group}
                options={options}
                active={active}
                listId={listId}
                onHover={setActive}
                note={group === "Addresses" && addresses.error ? addressNote(addresses.error) : null}
              />
            ))}
            {!pending && options.length === 0 && !addresses.error ? (
              <p className={styles.dropdownNote}>Nothing found for “{typed}”.</p>
            ) : null}
          </div>
          {note ? <p className={styles.dropdownNote}>{note}</p> : null}
          <div className={styles.dropdownFooter}>
            <button
              type="button"
              className={styles.dropPin}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onDropPin();
                finish();
              }}
            >
              <MapPinPlus size={14} aria-hidden /> Can't find it? Drop a pin on the map
            </button>
            <span className={styles.keys} aria-hidden>
              <kbd>↑↓</kbd> move <kbd>↵</kbd> open
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ResultGroup({
  group,
  options,
  active,
  listId,
  onHover,
  note,
}: {
  group: Group;
  options: Option[];
  active: number;
  listId: string;
  onHover: (i: number) => void;
  note: string | null;
}) {
  const mine = options.map((o, i) => ({ o, i })).filter(({ o }) => o.group === group);
  if (!mine.length && !note) return null;
  return (
    // biome-ignore lint/a11y/useSemanticElements: a listbox's options are grouped with role=group; a fieldset would be a form
    <div role="group" aria-label={group}>
      <p className={styles.groupLabel}>{group}</p>
      {note ? <p className={styles.dropdownNote}>{note}</p> : null}
      {mine.map(({ o, i }) => (
        // biome-ignore lint/a11y/useKeyWithClickEvents: the field owns the keyboard (aria-activedescendant)
        <div
          key={o.key}
          id={`${listId}-${o.key}`}
          role="option"
          tabIndex={-1}
          aria-selected={i === active}
          className={styles.option}
          onMouseDown={(e) => e.preventDefault()}
          onMouseMove={() => onHover(i)}
          onClick={o.pick}
        >
          <span className={styles.optionIcon}>{o.icon}</span>
          <span className={styles.stack}>
            <span>{o.title}</span>
            {o.subtitle ? <span className={styles.mutedInline}>{o.subtitle}</span> : null}
          </span>
          {o.tag ? <span className={`${styles.tag} ${styles[`tag_${o.tag.tone}`]}`}>{o.tag.label}</span> : null}
        </div>
      ))}
    </div>
  );
}
